import { useEffect, useState } from "react";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AssetType, SourceType } from "@nendaTypes/cms/CmsAssetType";
import { CMSAsset } from "@client/models/CMS/cmsAsset";
import { cmsService } from "@client/http/cms.service";
import {
  buildCmsAssetsQuery,
  CmsAssetsQuery,
} from "@client/http/cmsAssetQuery";

export const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 300;
const RESULTS_PER_QUERY = 10;
// The content API only searches localized titles in one given language.
export const LOCALIZED_TITLE_LANGUAGES = ["en", "sv"];

interface CmsAssetSearchFilter {
  type: AssetType[];
  sourceType: SourceType[];
}

const fetchAssets = (query: CmsAssetsQuery): Observable<CMSAsset[]> =>
  cmsService.getAssets(buildCmsAssetsQuery(query)).pipe(
    map((response) =>
      (response?.assets ?? []).map((asset) => new CMSAsset(asset))
    ),
    catchError(() => of([]))
  );

export const searchCmsAssets = (
  text: string,
  filter: CmsAssetSearchFilter
): Observable<CMSAsset[]> => {
  const base = { page: 1, pageSize: RESULTS_PER_QUERY, ...filter };
  return forkJoin([
    fetchAssets({ ...base, assetId: text }),
    fetchAssets({ ...base, originalTitle: text }),
    ...LOCALIZED_TITLE_LANGUAGES.map((titleLang) =>
      fetchAssets({ ...base, title: text, titleLang })
    ),
  ]).pipe(
    map((pages) => {
      const byId = new Map<string, CMSAsset>();
      pages.flat().forEach((asset) => {
        if (!byId.has(asset.assetId)) byId.set(asset.assetId, asset);
      });
      return [...byId.values()];
    })
  );
};

export function useCmsAssetSearch(text: string, filter: CmsAssetSearchFilter) {
  const [assets, setAssets] = useState<CMSAsset[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const search = text.trim();
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    if (search.length < MIN_SEARCH_LENGTH) {
      setAssets([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    let subscription;
    const timer = setTimeout(() => {
      subscription = searchCmsAssets(search, JSON.parse(filterKey)).subscribe(
        (found) => {
          setAssets(found);
          setIsSearching(false);
        }
      );
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      subscription?.unsubscribe();
    };
  }, [search, filterKey]);

  return { assets, isSearching };
}
